import React from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MicIcon from '@mui/icons-material/Mic';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DescriptionIcon from '@mui/icons-material/Description';
import { ChatUserModel } from 'types/chat-users/chat-user';
import Grid from '@mui/material/Grid2';
import { ChatUsersCustomizationOption } from 'types/company-platforms/customizationSettings';
import { Typography } from '@mui/material';
import { getMessagePreviewText, getOutcomingIcon } from 'utils/chat';
import { useIntl } from 'react-intl';

interface UserMessagePreviewProps {
  user: ChatUserModel;
  selected?: boolean;
  hoverStyles: ChatUsersCustomizationOption | null;
  selectedStyles: ChatUsersCustomizationOption | null;
  unreadStyles: ChatUsersCustomizationOption | null;
  defaultStyles: ChatUsersCustomizationOption | null;
}

//* Icons
const fileTypes = ['pdf', 'xlsx', 'xls', 'docx', 'doc', 'pdf'];
const iconStyle = { width: 14, height: 14, color: 'grey.800' };

const mediaTypeIconMap: Record<string, React.ElementType> = {
  location: LocationOnIcon,
  audio: MicIcon,
  image: ImageIcon,
  contacts: PersonIcon,
  template: StickyNote2Icon,
  video: VideoCameraBackIcon,
  zip: FolderZipIcon,
};
const getMediaIcon = (mediaType?: string | null) => {
  if (!mediaType) return null;

  const lowerMediaType = mediaType.toLowerCase();
  // If type exists – return existing icon
  if (mediaTypeIconMap[lowerMediaType]) {
    const IconComponent = mediaTypeIconMap[lowerMediaType];
    return <IconComponent sx={iconStyle} />;
  }
  if (fileTypes.includes(lowerMediaType)) {
    return <DescriptionIcon sx={iconStyle} />;
  }
  return null;
};

const UserMessagePreview: React.FC<UserMessagePreviewProps> = ({
  user,
  selected,
  hoverStyles,
  selectedStyles,
  unreadStyles,
  defaultStyles,
}) => {
  const outcomingIcon =
    user.LastMessageType === 'outcoming' && getOutcomingIcon(user.OutcomingLastMessageStatus);
  const mediaIcon = getMediaIcon(user.LastMessageMediaType);
  const intl = useIntl();

  const computeTextColor = () => {
    if (selected) {
      return selectedStyles?.value === 'custom'
        ? selectedStyles.customColor.message + ' !important'
        : // : 'text.secondary';
          undefined;
    }
    if (user.UnreadMessagesNumber && user.UnreadMessagesNumber > 0) {
      return unreadStyles?.value === 'custom'
        ? unreadStyles.customColor.message + ' !important'
        : // : 'text.secondary';
          undefined;
    }
    return defaultStyles?.value === 'custom'
      ? defaultStyles.customColor.message + ' !important'
      : // : 'text.secondary';
        undefined;
  };

  return (
    <Grid container columnSpacing={1} flexWrap="nowrap">
      {outcomingIcon && <Grid size="auto">{outcomingIcon}</Grid>}
      {mediaIcon && <Grid size="auto">{mediaIcon}</Grid>}
      <Grid size="grow">
        <Typography
          variant="caption"
          component="p"
          sx={{
            '.MuiListItemButton-root:not(.Mui-selected):hover &': {
              color:
                hoverStyles?.value === 'custom'
                  ? hoverStyles.customColor.message + ' !important'
                  : // : 'rgba(0, 0, 0, 0.6) !important',
                    undefined,
            },
            color: computeTextColor(),
            WebkitTransition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            maxWidth: '100%',
            lineHeight: 1.25,
            wordBreak: 'break-all',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {getMessagePreviewText(user, intl)}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default UserMessagePreview;
